<template>
  <div
    v-if="visible"
    class="interaction-layer"
    :class="{
      'interaction-layer--feedback': !!feedback,
      'interaction-layer--h5': isH5,
      'interaction-layer--landscape': isH5 && isLandscape,
    }"
    :style="{ zIndex: overlayZIndex }"
  >
    <div class="interaction-layer__mask" aria-hidden="true"></div>
    <section
      ref="dialog"
      class="interaction-layer__card"
      role="dialog"
      aria-modal="true"
      :aria-label="dialogLabel"
      :aria-busy="submitting ? 'true' : 'false'"
      tabindex="-1"
      @keydown="handleDialogKeydown"
    >
      <div class="interaction-layer__header">
        <div class="interaction-layer__header-main">
          <span class="interaction-layer__eyebrow">互动练习</span>
          <span class="interaction-layer__index"
            >第 {{ queueIndex + 1 }} 题 <i>/</i> 共 {{ groupTotal }} 题</span
          >
        </div>
        <div class="interaction-layer__header-tags">
          <span class="interaction-layer__type">{{ questionTypeLabel }}</span>
          <span v-if="!canSkip" class="interaction-layer__required">必答</span>
        </div>
      </div>
      <div
        class="interaction-layer__progress"
        aria-hidden="true"
      >
        <span :style="{ width: `${progressValue}%` }"></span>
      </div>
      <div v-if="feedback" class="interaction-layer__result" :class="`interaction-layer__result--${feedbackTone}`" role="status" aria-live="polite">
        <span class="interaction-layer__result-icon" aria-hidden="true">{{ feedbackIcon }}</span>
        <div>
          <strong>{{ feedbackTitle }}</strong>
          <span>{{ feedbackDetail }}</span>
        </div>
      </div>
      <div class="interaction-layer__body">
        <component
          v-if="rendererComponent"
          :is="rendererComponent"
          :key="current.id"
          :view="current.view"
          :env="rendererEnv"
          :feedback="feedback"
          :typeKey="current.type_key"
          @session-changed="handleSessionChanged"
        />
      </div>
      <div class="interaction-layer__footer">
        <p v-if="!feedback && !sessionComplete" class="interaction-layer__hint" role="status">
          请选择答案后提交
        </p>
        <span v-else class="interaction-layer__footer-spacer"></span>
        <button
          v-if="!feedback && canSkip"
          type="button"
          class="interaction-layer__btn interaction-layer__btn--ghost"
          :disabled="submitting"
          @click="handleSkip"
        >
          暂时跳过
        </button>
        <button
          v-if="!feedback"
          type="button"
          class="interaction-layer__btn interaction-layer__btn--primary"
          :disabled="!canSubmit"
          @click="handleSubmit"
        >
          {{ submitLabel }}
        </button>
        <button
          v-else
          type="button"
          class="interaction-layer__btn interaction-layer__btn--primary"
          @click="handleContinue"
        >
          {{ continueLabel }}
        </button>
      </div>
    </section>
  </div>
</template>

<script>
// 录播弹题播放器编排层：mounted 拉取当前课时弹题列表 → 按段(videoIndex)过滤/分组建 cue →
// 挂 metadata TextTrack(cuechange 主路径) + timeupdate 兜底 → 播放到点暂停播放器弹题卡片 →
// 作答/跳过后继续播放。行为契约见 docs/superpowers/plans/2026-07-16-interactive-video-player-
// integration-impl-plan.md Task 2；渲染器契约见 packages/interactions/src/contract.md。
//
// 降级铁律（本组件贯穿始终）：任何故障（渲染器缺失/渲染异常/接口失败）一律 console.error/warn
// 后自动跳过继续播放，绝不阻塞播放——宁可弹题功能不生效，也不能让学生卡在播放页看不了课。
//
// 挂载点/仿写范式：packages/components/src/components/shopping-cart/Index.vue（自包含
// :dplayer 组件，自管数据与生命周期，不依赖父级状态）。实际挂线（CustomPlayer.vue 内
// `.custom-player-container` 与 shopping-cart 同级）是 Task 3 的工作，本组件独立可测。
import {
  getInteractionList,
  postInteractionTrigger,
  postInteractionAnswer,
  postInteractionSkip,
} from "@saas/service-api/src/interaction";
import { getRenderer } from "@saas/interactions";
import {
  buildCues,
  shouldFire,
  EVENT_ANSWERED,
  EVENT_SKIPPED,
} from "./interaction-cue";

// z-index 阶梯（docs/superpowers/plans/2026-07-16-interactive-video-player-integration-impl-plan.md
// "已钉死的集成事实"表）：H5 原生播放钮 9999999999 / loading 999999999999。弹题浮层必须压过
// H5 原生播放钮，否则学生点它能绕过弹题恢复播放——取一个比两者都大的值。
const OVERLAY_Z_INDEX = 99999999999;

export default {
  name: "InteractionLayer",
  props: {
    // DPlayer 实例（CustomPlayer.vue:813-818 `new DPlayer({container, video:{}, hotkey:false})`
    // 的产物），本组件只用到 .video / .pause() / .play() / .on()。
    dplayer: {
      type: Object,
      required: true,
    },
    lessonId: {
      type: [Number, String],
      required: true,
    },
    // 父级可整体关闸（如未来的 A/B 或课时级开关）；关闸时不拉取列表、不弹题。
    enabled: {
      type: Boolean,
      default: true,
    },
    // H5 普通横屏下播放器可能高于首屏，需要把答题层提升到视口定位。
    // 由父组件传入明确区分 H5 与桌面端窄窗口，避免单靠尺寸误伤桌面布局。
    isH5: {
      type: Boolean,
      default: false,
    },
    // H5 横屏可能由页面旋转模拟，CSS orientation 查询不一定同步；使用播放器状态保证布局准确。
    isLandscape: {
      type: Boolean,
      default: false,
    },
    // 当前段，0 基（= replay/Index.vue 的 currentIndex）。与后端 item.video_index 同基准
    // 直接相等比较，不做 +1 偏移——见 interaction-cue.js 头部大注释的实测结论。
    videoIndex: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      overlayZIndex: OVERLAY_Z_INDEX,
      allItems: [],
      groups: [],
      groupsBySecond: new Map(),
      activeGroup: null,
      queueIndex: 0,
      current: null,
      feedback: null,
      sessionAnswer: null,
      sessionComplete: false,
      submitting: false,
      questionShownAt: 0,
      track: null,
      trackAttempted: false,
      timeupdateRegistered: false,
      returnFocusElement: null,
      destroyed: false,
    };
  },
  computed: {
    visible() {
      return !!this.activeGroup && !!this.current;
    },
    groupTotal() {
      return this.activeGroup ? this.activeGroup.items.length : 0;
    },
    rendererComponent() {
      return this.current ? getRenderer(this.current.type_key) : null;
    },
    rendererEnv() {
      return { mode: "answer", surface: "player-overlay" };
    },
    canSubmit() {
      return this.sessionComplete && !this.submitting;
    },
    canSkip() {
      if (!this.current) return false;
      const policy = this.current.policy;
      return !policy || policy.skippable !== false;
    },
    hasNextQuestion() {
      return !!(
        this.activeGroup &&
        this.queueIndex < this.activeGroup.items.length - 1
      );
    },
    continueLabel() {
      return this.hasNextQuestion ? "下一题" : "继续播放";
    },
    submitLabel() {
      return this.submitting ? "提交中…" : "提交答案";
    },
    questionTypeLabel() {
      const typeLabels = {
        single_choice: "单选题",
        multi_choice: "多选题",
        judgment: "判断题",
        fill_blank: "填空题",
        short_answer: "问答题",
        matching: "连线题",
      };
      return this.current && typeLabels[this.current.type_key]
        ? typeLabels[this.current.type_key]
        : "互动题";
    },
    progressValue() {
      if (!this.groupTotal) return 0;
      return Math.round(((this.queueIndex + 1) / this.groupTotal) * 100);
    },
    feedbackTone() {
      if (!this.feedback || this.feedback.right === 0) return "neutral";
      return this.feedback.right === 1 ? "correct" : "wrong";
    },
    feedbackTitle() {
      if (!this.feedback || this.feedback.right === 0) return "答案已提交";
      return this.feedback.right === 1 ? "回答正确" : "再想一想";
    },
    feedbackDetail() {
      if (!this.feedback || this.feedback.right === 0) {
        return "老师会在课后查看你的回答";
      }
      return this.feedback.right === 1
        ? "做得好，看看解析巩固一下吧"
        : "查看正确答案和解析后再继续";
    },
    feedbackIcon() {
      if (!this.feedback || this.feedback.right === 0) return "·";
      return this.feedback.right === 1 ? "✓" : "!";
    },
    dialogLabel() {
      return `${this.questionTypeLabel}，第 ${this.queueIndex + 1} 题，共 ${this.groupTotal} 题`;
    },
  },
  watch: {
    visible(val, oldVal) {
      if (val && !oldVal) {
        this.$nextTick(this.focusDialog);
      } else if (!val && oldVal) {
        this.$nextTick(this.restoreFocus);
      }
    },
    feedback(val, oldVal) {
      // 提交后渲染器会把原先的选项/输入框切到只读态。把焦点明确交给“下一题/继续播放”，
      // 既让键盘用户立即知道下一步，也避免焦点落在已禁用元素后逸出对话框。
      if (val && !oldVal) {
        this.$nextTick(() => {
          const dialog = this.$refs.dialog;
          const continueButton =
            dialog && dialog.querySelector(".interaction-layer__btn--primary");
          if (continueButton && typeof continueButton.focus === "function") {
            continueButton.focus();
          }
        });
      }
    },
    videoIndex() {
      this.rebuildCues();
    },
    enabled(val) {
      if (val) {
        this.fetchList();
        this.ensureTrack();
        // 挂载时 enabled=false 会在 mounted 早退、跳过 timeupdate 注册；后开闸要补上，
        // 否则兜底路径永远不工作（M1）。registerTimeupdate 内部有防重标志。
        this.registerTimeupdate();
      } else {
        // 父级整体关闸：若正在弹题，直接放行播放，不留半截 UI；后续 cue 全部失效
        if (this.activeGroup) this.resume();
        this.groups = [];
        this.syncTrackCues();
      }
    },
  },
  mounted() {
    if (!this.enabled) return;
    this.fetchList();
    this.ensureTrack();
    this.registerTimeupdate();
  },
  beforeDestroy() {
    this.destroyed = true;
    this.restoreFocus();
    if (this.track) {
      this.track.mode = "disabled";
      if (typeof this.track.removeEventListener === "function") {
        this.track.removeEventListener("cuechange", this.handleCueChange);
      }
    }
    // DPlayer（全局 CDN 脚本 window.DPlayer，saas-web/web/public/index.html 引入）没有
    // off()/removeListener API——实测 grep DPlayer.min.js 全文 `key:"off"` 零匹配，其
    // `on(e,t){this.events[e].push(t)}` 只增不减。dp.on 注册的 timeupdate 回调物理上无法
    // 解绑。用 destroyed 标记在回调入口短路，等效达成"清理"契约的实际意图：destroy 之后
    // 该监听不再产生任何副作用；track 侧的 cuechange 监听是真实 DOM EventTarget，可以
    // 真正 removeEventListener，两者合起来覆盖了本组件能触碰的所有监听来源。
  },
  errorCaptured(err) {
    console.error("[InteractionLayer] 渲染器抛出异常，降级自动跳过", err);
    if (this.activeGroup) {
      this.advance();
    }
    return false;
  },
  methods: {
    fetchList() {
      getInteractionList({ lesson_id: this.lessonId })
        .then((res) => {
          const items =
            res && res.code === 1 && res.data ? res.data.items : null;
          if (Array.isArray(items)) {
            this.allItems = items;
          } else {
            console.warn(
              "[InteractionLayer] getInteractionList 非预期返回，弹题静默禁用（不影响播放）",
              res
            );
            this.allItems = [];
          }
          this.rebuildCues();
        })
        .catch((err) => {
          console.warn(
            "[InteractionLayer] getInteractionList 请求失败，弹题静默禁用（不影响播放）",
            err
          );
          this.allItems = [];
          this.rebuildCues();
        });
    },
    rebuildCues() {
      this.groups = buildCues(this.allItems, this.videoIndex);
      this.syncTrackCues();
    },
    // timeupdate 兜底注册：与 shopping-cart/Index.vue、CustomPlayer.vue 同款写法，
    // dplayer.on(event, cb) 内部把 cb push 进事件数组。无条件注册（不依赖 track 是否
    // 建立成功），保证 cuechange 主路径失效/track 不可用（jsdom 等）时仍有一条能触发
    // 弹题的路径。防重标志必须有：DPlayer 无 off() API（见 beforeDestroy 注释），
    // mounted 与 enabled false→true watcher 两个入口都会走到这里，重复 on 会让同一
    // 回调堆积多次执行。
    registerTimeupdate() {
      if (this.timeupdateRegistered) return;
      if (this.dplayer && typeof this.dplayer.on === "function") {
        this.dplayer.on("timeupdate", this.handleTimeUpdate);
        this.timeupdateRegistered = true;
      }
    },
    // 挂 TextTrack：只在组件生命周期内尝试一次。replay/Index.vue:545-554 的 switchVideo
    // 实测只做 `this.video.src = url`（不重建 <video> 元素，见 DPlayer.min.js switchVideo
    // 源码），故 track 跨段切换存活，切段只需换 cue 集合、不需重挂 track。
    ensureTrack() {
      if (this.track || this.trackAttempted) return;
      this.trackAttempted = true;
      const video = this.dplayer && this.dplayer.video;
      if (!video || typeof video.addTextTrack !== "function") return;
      try {
        // 能力检测不能只靠上面的 typeof 判断：jsdom 里 addTextTrack 确实是个函数（typeof
        // 判"支持"），但实际调用会抛 "HTMLMediaElement.prototype.addTextTrack not
        // implemented"（jsdom 未实现该 API）。必须 try/catch 包住真实调用，仅凭 typeof
        // 在 jsdom 冒烟测试里会被误判为"支持"进而调用时抛错。这一 catch 分支正是 jsdom
        // 环境实际会走到的路径——冒烟测试因此天然覆盖 timeupdate 兜底路径；cuechange 真
        // 路径（需要真实浏览器 TextTrack 实现）留给 Task 5 真机验证。
        const track = video.addTextTrack("metadata");
        if (!track) return;
        track.addEventListener("cuechange", this.handleCueChange);
        this.track = track;
      } catch (e) {
        this.track = null;
      }
    },
    // 切段重建 cue：track.mode='disabled' 清空旧 cue → 按新段重新 addCue → mode 还原为
    // 'hidden'（不用 'showing'，避免浏览器把 metadata track 当字幕渲染出来）。
    syncTrackCues() {
      this.groupsBySecond = new Map();
      this.groups.forEach((group) => {
        this.groupsBySecond.set(group.triggerSecond, group);
      });
      if (!this.track) return;
      this.track.mode = "disabled";
      const existingCues = this.track.cues ? Array.from(this.track.cues) : [];
      existingCues.forEach((cue) => {
        try {
          this.track.removeCue(cue);
        } catch (e) {
          // 部分实现对已失效 cue 重复 removeCue 会抛错，忽略即可，不影响后续重建
        }
      });
      const CueCtor = window.VTTCue || window.TextTrackCue;
      if (CueCtor) {
        this.groups.forEach((group) => {
          try {
            const cue = new CueCtor(
              group.triggerSecond,
              group.triggerSecond + 0.5,
              ""
            );
            cue.id = String(group.triggerSecond);
            this.track.addCue(cue);
          } catch (e) {
            // 单个 cue 构建失败不影响其余 cue，也不影响 timeupdate 兜底路径
          }
        });
      }
      this.track.mode = "hidden";
    },
    handleCueChange() {
      if (this.destroyed || !this.track || !this.track.activeCues) return;
      for (let i = 0; i < this.track.activeCues.length; i++) {
        const cue = this.track.activeCues[i];
        const group = this.groupsBySecond.get(Number(cue.id));
        if (group) this.fireGroup(group);
      }
    },
    // timeupdate 兜底：cuechange 失效/track 不可用时的保险路径。与 cuechange 共享同一批
    // group 引用对象，group.fired 由 fireGroup 统一置位，两条路径天然互斥去重。
    handleTimeUpdate() {
      if (this.destroyed || !this.enabled) return;
      const video = this.dplayer && this.dplayer.video;
      if (!video) return;
      const currentTime = video.currentTime;
      const group = this.groups.find((g) => shouldFire(g, currentTime));
      if (group) this.fireGroup(group);
    },
    fireGroup(group) {
      if (!group || group.fired || this.activeGroup || this.destroyed) return;
      group.fired = true;
      this.activeGroup = group;
      if (this.dplayer && typeof this.dplayer.pause === "function") {
        this.dplayer.pause();
      }
      this.$emit("active-change", true);
      this.showQuestionAt(0);
    },
    // 展示组内第 index 题；渲染器缺失（未注册 type_key）直接降级跳过该题（不计入服务端
    // skip，只是本地不渲染——不影响其余题正常作答）；越界则整组结束、恢复播放。
    showQuestionAt(index) {
      if (!this.activeGroup || index >= this.activeGroup.items.length) {
        this.resume();
        return;
      }
      const item = this.activeGroup.items[index];
      if (!getRenderer(item.type_key)) {
        console.error(
          `[InteractionLayer] 未注册题型 type_key=${item.type_key}（interaction id=${item.id}），降级自动跳过`
        );
        this.showQuestionAt(index + 1);
        return;
      }
      this.queueIndex = index;
      this.current = item;
      // CSS 伪横屏无法旋转系统键盘；输入型题目由播放页临时切回竖屏。
      this.$emit(
        "input-mode-change",
        item.type_key === "fill_blank" || item.type_key === "short_answer"
      );
      this.feedback = null;
      this.sessionAnswer = null;
      this.sessionComplete = false;
      this.submitting = false;
      this.questionShownAt = Date.now();
      // fire-and-forget：触发上报失败不影响学生继续作答
      postInteractionTrigger({ interaction_id: item.id }).catch((err) => {
        console.warn(
          "[InteractionLayer] postInteractionTrigger 上报失败（fire-and-forget，不影响作答）",
          err
        );
      });
      this.$nextTick(this.focusDialog);
    },
    advance() {
      this.showQuestionAt(this.queueIndex + 1);
    },
    resume() {
      this.activeGroup = null;
      this.current = null;
      this.feedback = null;
      this.sessionAnswer = null;
      this.sessionComplete = false;
      this.submitting = false;
      this.$emit("input-mode-change", false);
      this.$emit("active-change", false);
      if (this.dplayer && typeof this.dplayer.play === "function") {
        this.dplayer.play();
      }
    },
    handleSessionChanged(payload) {
      this.sessionAnswer = payload && payload.answer;
      this.sessionComplete = !!(payload && payload.complete);
    },
    getFocusableElements() {
      const dialog = this.$refs.dialog;
      if (!dialog || typeof dialog.querySelectorAll !== "function") return [];
      return Array.from(
        dialog.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      );
    },
    focusDialog() {
      if (typeof document === "undefined") return;
      const dialog = this.$refs.dialog;
      if (!dialog) return;
      if (!this.returnFocusElement) {
        this.returnFocusElement = document.activeElement;
      }
      const [firstFocusable] = this.getFocusableElements();
      const target = firstFocusable || dialog;
      if (target && typeof target.focus === "function") target.focus();
    },
    restoreFocus() {
      const target = this.returnFocusElement;
      this.returnFocusElement = null;
      if (
        target &&
        typeof document !== "undefined" &&
        document.contains(target) &&
        typeof target.focus === "function"
      ) {
        target.focus();
      }
    },
    handleDialogKeydown(event) {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = this.getFocusableElements();
      const dialog = this.$refs.dialog;
      if (!focusable.length) {
        event.preventDefault();
        if (dialog && typeof dialog.focus === "function") dialog.focus();
        return;
      }
      const currentIndex = focusable.indexOf(document.activeElement);
      const lastIndex = focusable.length - 1;
      if (event.shiftKey && currentIndex <= 0) {
        event.preventDefault();
        focusable[lastIndex].focus();
      } else if (!event.shiftKey && (currentIndex === -1 || currentIndex >= lastIndex)) {
        event.preventDefault();
        focusable[0].focus();
      }
    },
    // F1：作答/跳过成功后回写条目本地状态。对象引用贯通 allItems→groups→current（buildCues
    // 无克隆），改 item.state 即同步改了 allItems 里的源条目。"从头学"（CustomPlayer:167
    // handleReply → afresh → replay/Index.vue:621-632 重置 currentIndex=0，不刷新页面）
    // 会经 videoIndex watcher → rebuildCues 从 allItems 重建 fired:false 的新组——若不
    // 回写 event，已答/已跳过的题会重新入组重弹，违反"排除 event===1||2"契约。回写后
    // buildCues 的过滤规则自然排除，与刷新页面后服务端 list 下发的状态保持一致（feedback
    // 也一并塞进 state.feedback，对齐服务端回显形状）。
    markItemState(item, event, feedback) {
      if (!item) return;
      if (!item.state || typeof item.state !== "object") item.state = {};
      item.state.event = event;
      if (feedback) item.state.feedback = feedback;
    },
    handleSubmit() {
      if (!this.canSubmit || !this.current) return;
      this.submitting = true;
      const item = this.current;
      const durationMs = Date.now() - this.questionShownAt;
      postInteractionAnswer({
        interaction_id: item.id,
        answer: this.sessionAnswer,
        duration_ms: durationMs,
      })
        .then((res) => {
          this.submitting = false;
          if (res && res.code === 1) {
            // 回写已答状态（F1，防"从头学"重弹）+ 响应作为 feedback 传回渲染器：
            // 高亮对错/正解/解析，并显示"继续"按钮
            this.markItemState(item, EVENT_ANSWERED, res.data);
            this.feedback = res.data;
          } else {
            console.error(
              "[InteractionLayer] postInteractionAnswer 返回非预期结果，降级自动跳过",
              res
            );
            this.$message.error(res.message);
            this.advance();
          }
        })
        .catch((err) => {
          this.submitting = false;
          console.error(
            "[InteractionLayer] postInteractionAnswer 请求异常，降级自动跳过（不卡死播放）",
            err
          );
          this.advance();
        });
    },
    handleContinue() {
      this.advance();
    },
    handleSkip() {
      if (!this.current) return;
      const item = this.current;
      // fire-and-forget（advance 不等响应），但成功后回写已跳过状态（F1）——失败不回写：
      // 服务端没记录到 skip，"从头学"重弹反而与服务端状态一致
      postInteractionSkip({ interaction_id: item.id })
        .then((res) => {
          if (res && res.code === 1) {
            this.markItemState(item, EVENT_SKIPPED);
          }
        })
        .catch((err) => {
          console.warn(
            "[InteractionLayer] postInteractionSkip 请求失败（不阻塞继续播放）",
            err
          );
        });
      this.advance();
    },
  },
};
</script>

<style scoped lang="scss">
// 桌面端覆盖播放器自身；手机端例外地升级为视口级答题层，避免在 16:9 播放器里挤压题目和操作区。
// 该例外只在窄屏生效，不改变播放器本身的布局或桌面端定位方式。
.interaction-layer {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.interaction-layer__mask {
  position: absolute;
  inset: 0;
  background: var(--mg-bg-overlay, rgba(3, 16, 29, 0.72));
  backdrop-filter: blur(3px);
}

.interaction-layer__card {
  position: relative;
  display: flex;
  flex-direction: column;
  width: min(90%, 672px);
  max-height: min(84%, 620px);
  overflow: hidden;
  box-sizing: border-box;
  color: var(--mg-text-primary, #0d1400);
  background: var(--mg-bg-card, #fff);
  border: 1px solid var(--mg-border-subtle, rgba(0, 0, 0, 0.08));
  border-radius: 18px;
  box-shadow: 0 24px 72px rgba(0, 0, 0, 0.34), 0 4px 18px rgba(0, 67, 82, 0.2);
  outline: none;
  --interaction-secondary-text: #52646e;
  --interaction-accent-text: #047857;
  --interaction-warning-text: #a84a00;
  --interaction-danger-text: #b42335;
  --qz-primary: var(--mg-brand-primary, #00c89d);
  --qz-primary-hover-bg: rgba(var(--mg-brand-primary-rgb, 0, 200, 157), 0.06);
  --qz-primary-selected-bg: rgba(var(--mg-brand-primary-rgb, 0, 200, 157), 0.12);
  --qz-primary-focus-ring: rgba(var(--mg-brand-primary-rgb, 0, 200, 157), 0.28);
  --qz-primary-shadow: rgba(var(--mg-brand-primary-rgb, 0, 200, 157), 0.12);
  --qz-correct: var(--mg-success-primary, #00c89d);
  --qz-wrong: var(--mg-danger-primary, #f03349);
  --qz-text: var(--mg-text-primary, #0d1400);
  --qz-option-padding: 13px 15px;
  --qz-option-min-height: 52px;
  --qz-option-radius: 13px;
  --qz-control-radius: 12px;
  --qz-input-height: 42px;
  --qz-input-radius: 10px;
  --qz-input-background: var(--mg-control-bg, #fff);
  --qz-textarea-min-height: 132px;
  --qz-option-border: var(--mg-border-subtle, rgba(0, 0, 0, 0.08));
  --qz-option-background: var(--mg-control-bg, #fff);
  --qz-option-shadow: 0 1px 0 rgba(15, 68, 76, 0.03);
  animation: interaction-layer-enter 180ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
}

.interaction-layer__card:focus-visible {
  box-shadow: 0 0 0 4px rgba(var(--mg-brand-primary-rgb, 0, 200, 157), 0.3), 0 24px 72px rgba(0, 0, 0, 0.34);
}

.interaction-layer__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 17px 22px 14px;
  background: var(--mg-bg-surface, #f8fafc);
}

.interaction-layer__header-main,
.interaction-layer__header-tags {
  display: flex;
  align-items: center;
}

.interaction-layer__header-main {
  min-width: 0;
  gap: 10px;
}

.interaction-layer__header-tags {
  flex: 0 0 auto;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
}

.interaction-layer__eyebrow {
  flex: 0 0 auto;
  padding: 4px 8px;
  color: var(--interaction-accent-text);
  background: var(--mg-brand-soft-bg, rgba(0, 200, 157, 0.12));
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
}

.interaction-layer__index {
  min-width: 0;
  color: var(--interaction-secondary-text);
  font-size: 14px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.interaction-layer__index i {
  padding: 0 2px;
  color: var(--interaction-secondary-text);
  font-style: normal;
}

.interaction-layer__type,
.interaction-layer__required {
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
}

.interaction-layer__type {
  color: var(--mg-text-primary, #0d1400);
  background: var(--mg-control-hover-bg, #f8fafc);
}

.interaction-layer__required {
  color: var(--interaction-warning-text);
  background: var(--mg-warning-soft-bg, rgba(255, 109, 0, 0.1));
  border: 1px solid rgba(var(--mg-warning-primary-rgb, 255, 109, 0), 0.24);
}

.interaction-layer__progress {
  height: 4px;
  overflow: hidden;
  background: var(--mg-brand-soft-bg, rgba(0, 200, 157, 0.12));
}

.interaction-layer__progress span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: var(--mg-brand-primary, #00c89d);
  box-shadow: 0 0 10px rgba(var(--mg-brand-primary-rgb, 0, 200, 157), 0.42);
  transition: width 220ms ease-out;
}

.interaction-layer__result {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 14px 22px 0;
  padding: 10px 12px;
  border-radius: 13px;
  font-size: 13px;
  line-height: 1.45;
}

.interaction-layer__result-icon {
  display: grid;
  flex: 0 0 auto;
  width: 25px;
  height: 25px;
  place-items: center;
  border-radius: 50%;
  color: var(--mg-text-inverse, #fff);
  font-size: 15px;
  font-weight: 800;
}

.interaction-layer__result strong,
.interaction-layer__result span:not(.interaction-layer__result-icon) {
  display: block;
}

.interaction-layer__result strong {
  color: var(--mg-text-primary, #0d1400);
  font-size: 14px;
}

.interaction-layer__result span:not(.interaction-layer__result-icon) {
  margin-top: 1px;
  color: var(--interaction-secondary-text);
}

.interaction-layer__result--correct {
  background: rgba(var(--mg-success-primary-rgb, 0, 200, 157), 0.1);
  border: 1px solid rgba(var(--mg-success-primary-rgb, 0, 200, 157), 0.24);
}

.interaction-layer__result--correct .interaction-layer__result-icon {
  background: var(--mg-success-primary, #00c89d);
}

.interaction-layer__result--wrong {
  background: rgba(var(--mg-danger-primary-rgb, 240, 51, 73), 0.1);
  border: 1px solid rgba(var(--mg-danger-primary-rgb, 240, 51, 73), 0.24);
}

.interaction-layer__result--wrong .interaction-layer__result-icon {
  background: var(--mg-danger-primary, #f03349);
}

.interaction-layer__result--neutral {
  background: var(--mg-brand-soft-bg, rgba(0, 200, 157, 0.12));
  border: 1px solid rgba(var(--mg-brand-primary-rgb, 0, 200, 157), 0.24);
}

.interaction-layer__result--neutral .interaction-layer__result-icon {
  background: var(--mg-brand-primary, #00c89d);
}

.interaction-layer__body {
  flex: 1 1 auto;
  min-height: 0;
  padding: 18px 22px 10px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.interaction-layer__footer {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 15px 22px 18px;
  background: var(--mg-bg-surface, #f8fafc);
  border-top: 1px solid var(--mg-border-subtle, rgba(0, 0, 0, 0.08));
}

.interaction-layer__hint {
  min-width: 0;
  margin: 0 auto 0 0;
  color: var(--mg-text-primary, #0d1400);
  font-size: 12px;
}

.interaction-layer__footer-spacer {
  flex: 1 1 auto;
}

.interaction-layer__btn {
  min-width: 104px;
  height: 48px;
  padding: 0 18px;
  border: 1px solid transparent;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: transform 160ms ease-out, box-shadow 160ms ease-out, background-color 160ms ease-out, border-color 160ms ease-out;
}

.interaction-layer__btn:focus-visible {
  outline: 3px solid rgba(var(--mg-brand-primary-rgb, 0, 200, 157), 0.28);
  outline-offset: 2px;
}

.interaction-layer__btn:not(:disabled):hover {
  transform: translateY(-1px);
}

.interaction-layer__btn:not(:disabled):active {
  transform: translateY(0);
}

.interaction-layer__btn--ghost {
  color: var(--mg-text-primary, #0d1400);
  background: var(--mg-control-bg, #fff);
  border-color: var(--mg-border-subtle, rgba(0, 0, 0, 0.08));
}

.interaction-layer__btn--ghost:not(:disabled):hover {
  color: var(--interaction-accent-text);
  background: var(--mg-brand-soft-bg, rgba(0, 200, 157, 0.12));
  border-color: var(--mg-brand-primary, #00c89d);
}

.interaction-layer__btn--ghost:disabled,
.interaction-layer__btn--primary:disabled {
  cursor: not-allowed;
  opacity: 0.48;
  box-shadow: none;
}

.interaction-layer__btn--primary {
  color: var(--mg-text-primary, #0d1400);
  background: var(--mg-brand-action-bg, #00c89d);
  box-shadow: 0 7px 16px rgba(var(--mg-brand-primary-rgb, 0, 200, 157), 0.24);
}

.interaction-layer__btn--primary:not(:disabled):hover {
  background: var(--mg-brand-action-hover-bg, #059669);
  box-shadow: 0 10px 20px rgba(var(--mg-brand-primary-rgb, 0, 200, 157), 0.3);
}

@keyframes interaction-layer-enter {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 767px) {
  .interaction-layer {
    position: fixed;
    width: 100vw;
    height: 100vh;
    height: 100dvh;
    align-items: flex-end;
  }

  .interaction-layer__mask {
    background: var(--mg-bg-overlay, rgba(3, 16, 29, 0.76));
  }

  .interaction-layer__card {
    width: 100%;
    height: auto;
    max-width: none;
    max-height: 78vh;
    max-height: 78dvh;
    border-radius: 24px 24px 0 0;
  }

  .interaction-layer__header {
    align-items: center;
    gap: 10px;
    padding: 16px 18px 13px;
  }

  .interaction-layer__header-main {
    display: flex;
  }

  .interaction-layer__eyebrow {
    margin-right: 0;
  }

  .interaction-layer__index {
    font-size: 13px;
  }

  .interaction-layer__header-tags {
    max-width: 110px;
  }

  .interaction-layer__result {
    margin: 12px 18px 0;
  }

  .interaction-layer__body {
    padding: 16px 18px 10px;
  }

  .interaction-layer__footer {
    flex-wrap: wrap;
    padding: 13px 18px calc(14px + env(safe-area-inset-bottom));
  }

  .interaction-layer__hint {
    flex: 1 0 100%;
  }

  .interaction-layer__footer-spacer {
    display: none;
  }

  .interaction-layer__btn {
    flex: 1 1 0;
    min-width: 0;
  }
}

@media (max-height: 767px) and (orientation: landscape) {
  .interaction-layer {
    align-items: center;
  }

  .interaction-layer--h5 {
    position: fixed;
    inset: 0;
    width: 100vw;
    height: 100vh;
    height: 100dvh;
  }

  .interaction-layer__card {
    width: min(90vw, 672px);
    max-height: calc(100dvh - 24px);
    border-radius: 18px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .interaction-layer__card,
  .interaction-layer__progress span,
  .interaction-layer__btn {
    animation: none;
    transition: none;
  }
}
</style>

<style lang="scss">
// H5 横屏既可能来自系统方向，也可能由微信等容器给整页加 replay-landscape 后旋转。
// 两种状态统一把弹题层约束回播放器坐标系，避免继续套用竖屏底部抽屉。
.interaction-layer--h5.interaction-layer--landscape,
.replay-landscape .interaction-layer--h5 {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  padding: 12px 16px;
  box-sizing: border-box;
  align-items: center;
}

.interaction-layer--h5.interaction-layer--landscape .interaction-layer__card,
.replay-landscape .interaction-layer--h5 .interaction-layer__card {
  width: min(82%, 720px);
  max-width: 720px;
  max-height: 100%;
  border-radius: 18px;
}

.interaction-layer--h5.interaction-layer--landscape .interaction-layer__header,
.replay-landscape .interaction-layer--h5 .interaction-layer__header {
  padding: 12px 18px 10px;
}

.interaction-layer--h5.interaction-layer--landscape .interaction-layer__body,
.replay-landscape .interaction-layer--h5 .interaction-layer__body {
  padding: 12px 18px 8px;
}

.interaction-layer--h5.interaction-layer--landscape .interaction-layer__footer,
.replay-landscape .interaction-layer--h5 .interaction-layer__footer {
  flex-wrap: nowrap;
  padding: 10px 18px 12px;
}

.interaction-layer--h5.interaction-layer--landscape .interaction-layer__hint,
.replay-landscape .interaction-layer--h5 .interaction-layer__hint {
  flex: 1 1 auto;
}

html.dark .interaction-layer__card {
  --interaction-secondary-text: var(--mg-text-muted, #94a3b8);
  --interaction-accent-text: var(--mg-brand-primary, #34d399);
  --interaction-warning-text: var(--mg-warning-primary, #fdba74);
  --interaction-danger-text: var(--mg-danger-primary, #fb7185);
}

.interaction-layer__card .qz-stem {
  margin-bottom: 16px;
  color: var(--mg-text-primary, #0d1400);
  font-size: 18px;
  font-weight: 600;
  line-height: 1.65;
}

.interaction-layer__card .qz-feedback {
  margin-top: 16px;
  padding: 13px 14px;
  color: var(--mg-text-primary, #0d1400);
  background: var(--mg-bg-surface, #f8fafc);
  border: 1px solid var(--mg-border-subtle, rgba(0, 0, 0, 0.08));
  border-radius: 13px;
  font-size: 14px;
}

.interaction-layer__card .qz-feedback-result {
  margin-bottom: 5px;
  color: inherit;
  font-size: 15px;
  font-weight: 700;
}

.interaction-layer__card .qz-feedback-right .qz-feedback-result {
  color: var(--interaction-accent-text);
}

.interaction-layer__card .qz-feedback-wrong .qz-feedback-result {
  color: var(--interaction-danger-text);
}

.interaction-layer__card .qz-analysis {
  margin-top: 8px;
  color: var(--interaction-secondary-text);
  opacity: 1;
}

@media (max-width: 767px) {
  .interaction-layer__card .qz-stem {
    font-size: 16px;
  }
}

.interaction-layer--h5 .qz-option-text {
  font-size: 16px;
  line-height: 1.65;
}

</style>
